import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, ChangeDetectorRef, ViewChild, ElementRef, AfterViewChecked, OnDestroy } from '@angular/core';
import { SparePartModel } from 'src/app/models/spare-part-model/spare-part-model';
import { environment } from 'src/environments/environment';
import { SparePartsService } from 'src/app/services/spareParts/spare-parts.service';
import { OrdersService } from 'src/app/services/orders/orders.service';
import { ProvidersService } from 'src/app/services/providers/providers.service';
import { ProviderModel } from 'src/app/models/provider-model/provider-model';
import { CurrencyFormatter } from '../../../utils/currency-formatter';
import { ALDANA_COLOR_OPTIONS } from 'src/app/constants/aldana-colors';
import { DEFAULT_SPARE_PART_SHIPPING_RECEIPT } from 'src/app/constants/spare-part-defaults';
import { RETURN_CARRIER_OPTIONS, RETURN_LOG_STATUS_OPTIONS, RETURN_REASON_OPTIONS, RETURN_STATUS_OPTIONS, ML_STATUS_OPTIONS, FRETE_DEV_OPTIONS } from 'src/app/utils/return-options.util';
import { collectLowSaleRatioViolations, getSaleValueValidationError } from 'src/app/utils/spare-part-validation.util';
import { applyPostEmergentCommentDuplicateDefaults, normalizeFantUrgency, defaultFantUrgency, FantUrgency } from 'src/app/utils/spare-part-payload.util';
import { getReceptionStatusFieldClass } from 'src/app/utils/spare-part-reception.util';
import { readApiErrorMessage, readIsDamagedState, readNeedReviewFlag, SPARE_PART_DELETE_BLOCKED_MESSAGE, sparePartHasPackageOrPrintedLabel } from 'src/app/utils/spare-part-packaging.util';
import { SPARE_PART_DAMAGED_OPTIONS } from 'src/app/constants/spare-part-damaged-states';
import { formatWallClockDateTimeForInput, parseWallClockDateTimeInput } from 'src/app/utils/wall-clock-datetime.util';

@Component({
  selector: 'app-spare-part-modal',
  templateUrl: './spare-part-modal.component.html',
  styleUrls: ['./spare-part-modal.component.css']
})
export class SparePartModalComponent implements OnChanges, AfterViewChecked, OnDestroy {

  @Input() selectedSparePart: SparePartModel | null = null;
  @Input() duplicateMode = false;
  @Input() duplicateTargetOrderNumber = '';
  @Output() close = new EventEmitter<void>();
  @Output() sparePartUpdated = new EventEmitter<SparePartModel>();
  @Output() sparePartDeleted = new EventEmitter<SparePartModel>();
  @Output() duplicateRequested = new EventEmitter<SparePartModel>();
  @Output() duplicateTargetOrderNumberChange = new EventEmitter<string>();
  @ViewChild('shippingReceiptInput', { static: false }) shippingReceiptInput!: ElementRef<HTMLInputElement>;
  @ViewChild('modalContent', { static: false }) modalContent!: ElementRef<HTMLDivElement>;
  
  sparePartDeliveryStatus = environment.sparePartDeliveryStatus;
  providers: ProviderModel[] = [];
  shippingSparePartOptions = environment.shippingSparePartOptions;
  sparePartReceptionStatusOptions = (environment as any).sparePartReceptionStatusOptions ?? ['LLEGO', 'NO-LLEGO'];
  getReceptionStatusFieldClass = getReceptionStatusFieldClass;
  selectedSparePartArrivalDate: string = '';
  selectedSparePartExportDate: string = '';
  selectedSparePartPhysicalArrivalDateTime: string = '';
  showSuccessMessage: boolean = false;
  showErrorMessage: boolean = false;
  successMessage: string = '';
  errorMessage: string = '';
  showAldanaForm = false;
  aldanaFechaComunicadoInput = '';
  duplicateTargetOrderErrorMessage = '';
  showLowSaleRatioConfirmModal = false;
  lowSaleRatioViolationLabels: string[] = [];
  private pendingDuplicateAction: (() => void) | null = null;
  aldanaColorOptions = ALDANA_COLOR_OPTIONS;
  readonly returnStatusOptions = RETURN_STATUS_OPTIONS;
  readonly returnLogStatusOptions = RETURN_LOG_STATUS_OPTIONS;
  readonly returnReasonOptions = RETURN_REASON_OPTIONS;
  readonly returnCarrierOptions = RETURN_CARRIER_OPTIONS;
  readonly mlStatusOptions = ML_STATUS_OPTIONS;
  readonly freteDevOptions = FRETE_DEV_OPTIONS;
  readonly damagedStateOptions = SPARE_PART_DAMAGED_OPTIONS;
  selectedSparePartMlDeadline = '';
  
  // Variable para almacenar el remito y forzar actualización
  _shippingReceipt: string = '';
  private lastShippingReceipt: string = '';
  /** Estado base comparable al abrir; evita carreras asíncronas al detectar cambios. */
  private initialComparableState: string | null = null;
  /** Número de exportación al abrir. No se pisa si la comparación general falla. */
  private exportNumberWhenOpened = '';

  constructor(
    private sparePartService: SparePartsService,
    private ordersService: OrdersService,
    private providersService: ProvidersService,
    private cdr: ChangeDetectorRef
  ) { }

  private text(value: unknown): string {
    return value == null ? '' : String(value).trim();
  }

  private normalizeReceptionStatus(value: unknown): string {
    const normalized = this.text(value).toUpperCase();
    if (!normalized) return 'NO-LLEGO';
    if (normalized === 'LLEGO' || normalized === 'LLEGÓ') return 'LLEGO';
    if (normalized === 'NO-LLEGO' || normalized === 'NO LLEGO' || normalized === 'NO_LLEGO' || normalized === 'AVERIA' || normalized === 'AVERÍA') return 'NO-LLEGO';
    return 'NO-LLEGO';
  }

  /**
   * Opciones de estado para el select.
   * Incluye el estado actual si no viene en el catálogo base
   * para evitar que el campo aparezca vacío al abrir el modal.
   */
  get sparePartStatusOptions(): string[] {
    const base = Array.isArray(this.sparePartDeliveryStatus) ? [...this.sparePartDeliveryStatus] : [];
    const current = (this.selectedSparePart?.productStatus || '').trim();
    if (current && !base.includes(current)) {
      return [current, ...base];
    }
    return base;
  }
  
  // Getter/Setter para shippingReceipt que fuerza actualización
  get shippingReceipt(): string {
    if (this.selectedSparePart) {
      return this.selectedSparePart.shippingReceipt || this._shippingReceipt || '';
    }
    return this._shippingReceipt || '';
  }
  
  set shippingReceipt(value: string) {
    this._shippingReceipt = value || '';
    if (this.selectedSparePart) {
      this.selectedSparePart.shippingReceipt = value || '';
    }
  }

  getDacTrackingUrl(remito: string | null | undefined): string {
    const trimmedRemito = (remito || '').trim();
    return trimmedRemito
      ? `${DEFAULT_SPARE_PART_SHIPPING_RECEIPT}${encodeURIComponent(trimmedRemito)}`
      : DEFAULT_SPARE_PART_SHIPPING_RECEIPT;
  }

  private updateInterval: any = null;
  
  ngOnInit(): void {
    this.loadProviders();

    if (this.selectedSparePart) {
      this.selectedSparePartArrivalDate = this.selectedSparePart.expectedArrivalDate 
        ? new Date(this.selectedSparePart.expectedArrivalDate).toISOString().split('T')[0]
        : '';
      this.selectedSparePart.receptionStatus = this.normalizeReceptionStatus(this.selectedSparePart.receptionStatus);
      this.selectedSparePartExportDate = this.toInputDate(this.selectedSparePart.fechaExportacion);
      this.selectedSparePartPhysicalArrivalDateTime = this.toInputDateTime(this.selectedSparePart.arrivalDate);
      
      // Inicializar el remito - verificar todas las posibles fuentes
      const remitoValue = this.selectedSparePart.shippingReceipt || 
                         (this.selectedSparePart as any).ShippingReceipt || 
                         (this.selectedSparePart as any).shippingReceipt || 
                         '';
      
      this._shippingReceipt = remitoValue;
      this.lastShippingReceipt = remitoValue;
      this.aldanaFechaComunicadoInput = this.toInputDate(this.selectedSparePart.fechaDeComunicado);
      this.selectedSparePartMlDeadline = this.toInputDate(this.selectedSparePart.mlDeadline);
      
      // CRÍTICO: Si no hay remito, intentar obtenerlo directamente desde el backend
      if (!remitoValue || remitoValue.trim() === '') {
        this.fetchShippingReceiptFromBackend();
      }
      
      // Forzar actualización múltiples veces con diferentes delays
      const delays = [0, 50, 100, 200, 300, 500, 1000, 2000];
      delays.forEach(delay => {
        setTimeout(() => {
          this.forceUpdateShippingReceiptInput(remitoValue);
        }, delay);
      });
      
      // Iniciar polling para verificar y actualizar el input cada 500ms
      if (this.updateInterval) {
        clearInterval(this.updateInterval);
      }
      this.updateInterval = setInterval(() => {
        this.checkAndUpdateInput();
      }, 500);
    }
  }

  private loadProviders(): void {
    this.providersService.getProviders().subscribe({
      next: (providers) => {
        this.providers = (providers || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      },
      error: (error) => {
        console.error('Error al cargar proveedores en modal de repuesto:', error);
        this.providers = [];
      }
    });
  }

  getProviderOptions(currentValue?: string): string[] {
    const catalog = this.providers
      .map(provider => (provider.name || '').trim())
      .filter(name => !!name);

    const current = (currentValue || '').trim();
    if (!current) {
      return Array.from(new Set(catalog));
    }

    return Array.from(new Set([current, ...catalog]));
  }
  
  // Método para obtener el remito directamente desde el backend
  private fetchShippingReceiptFromBackend(): void {
    if (!this.selectedSparePart || !this.selectedSparePart.orderId) {
      return;
    }
    
    // Usar el servicio de órdenes para obtener el repuesto actualizado
    // Necesitamos importar OrdersService
    // Por ahora, vamos a usar un enfoque diferente: verificar si hay packageNumber y obtener desde ahí
    
    if (this.selectedSparePart.packageNumber) {
      // Esto se manejará desde el componente padre, pero podemos intentar aquí también
    }
  }
  
  ngOnDestroy(): void {
    // Limpiar el intervalo cuando el componente se destruye
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }
  
  onShippingReceiptChange(value: string): void {
    this._shippingReceipt = value || '';
    if (this.selectedSparePart) {
      this.selectedSparePart.shippingReceipt = value || '';
    }
    this.lastShippingReceipt = value || '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Actualizar la fecha cuando cambie el repuesto seleccionado
    if (changes['selectedSparePart'] && this.selectedSparePart) {
      const currentValue = changes['selectedSparePart'].currentValue;
      
      // CRÍTICO: Crear una nueva referencia del objeto para forzar la detección de cambios
      if (currentValue) {
        // Crear una copia profunda para forzar la detección de cambios
        this.selectedSparePart = { ...currentValue };
        this.selectedSparePart.receptionStatus = this.normalizeReceptionStatus(this.selectedSparePart.receptionStatus);
        
        // Extraer el remito de todas las formas posibles
        const remitoValue = currentValue.shippingReceipt || 
                           (currentValue as any).ShippingReceipt || 
                           this._shippingReceipt || 
                           '';
        
        // Actualizar tanto el objeto como la variable local
        this.selectedSparePart.shippingReceipt = remitoValue;
        this._shippingReceipt = remitoValue;
        this.lastShippingReceipt = remitoValue;
        
        // Forzar detección de cambios múltiples veces
        this.cdr.detectChanges();
        
        // Usar setTimeout para forzar actualización después del renderizado
        setTimeout(() => {
          this.forceUpdateShippingReceiptInput(remitoValue);
        }, 0);
        
        setTimeout(() => {
          this.forceUpdateShippingReceiptInput(remitoValue);
        }, 100);
        
        setTimeout(() => {
          this.forceUpdateShippingReceiptInput(remitoValue);
        }, 300);
      }
      
      // Actualizar la fecha
      if (this.selectedSparePart.expectedArrivalDate) {
        this.selectedSparePartArrivalDate = new Date(this.selectedSparePart.expectedArrivalDate).toISOString().split('T')[0];
      }
      this.selectedSparePartExportDate = this.toInputDate(this.selectedSparePart.fechaExportacion);
      this.selectedSparePartPhysicalArrivalDateTime = this.toInputDateTime(this.selectedSparePart.arrivalDate);
      this.aldanaFechaComunicadoInput = this.toInputDate(this.selectedSparePart.fechaDeComunicado);
      this.selectedSparePartMlDeadline = this.toInputDate(this.selectedSparePart.mlDeadline);
      if (this.duplicateMode && !this.duplicateTargetOrderNumber?.trim()) {
        this.duplicateTargetOrderNumber = this.selectedSparePart.orderNumber || '';
        this.duplicateTargetOrderNumberChange.emit(this.duplicateTargetOrderNumber);
      }
      this.exportNumberWhenOpened = this.text(this.selectedSparePart.exportNumber);
      this.initialComparableState = this.buildComparableState();
    } else if (changes['selectedSparePart'] && !changes['selectedSparePart'].currentValue) {
      this.initialComparableState = null;
    }

    if ((changes['selectedSparePart'] && changes['selectedSparePart'].currentValue) || (changes['duplicateMode'] && this.duplicateMode)) {
      this.scrollToTopOfForm();
    }
  }

  private buildComparableState(): string {
    if (!this.selectedSparePart) return '';
    return JSON.stringify({
      sparePartId: this.selectedSparePart.sparePartId ?? null,
      sparePartName: this.text(this.selectedSparePart.sparePartName),
      sparePartNameEs: this.text(this.selectedSparePart.sparePartNameEs),
      sparePartNamePt: this.text(this.selectedSparePart.sparePartNamePt),
      productCode: this.text(this.selectedSparePart.productCode),
      providerName: this.text(this.selectedSparePart.providerName),
      productStatus: this.text(this.selectedSparePart.productStatus),
      shippingStatus: this.text(this.selectedSparePart.shippingStatus),
      sendYesOrNo: this.selectedSparePart.sendYesOrNo || 'false',
      receptionStatus: this.normalizeReceptionStatus(this.selectedSparePart.receptionStatus),
      returnStatus: this.text(this.selectedSparePart.returnStatus),
      returnLogStatus: this.text(this.selectedSparePart.returnLogStatus),
      returnReason: this.text(this.selectedSparePart.returnReason),
      returnCarrier: this.text(this.selectedSparePart.returnCarrier),
      nfd: this.text(this.selectedSparePart.nfd),
      mlStatus: this.text(this.selectedSparePart.mlStatus),
      freteDev: this.text(this.selectedSparePart.freteDev),
      newOrderNumber: this.text(this.selectedSparePart.newOrderNumber),
      returnDetails: this.text(this.selectedSparePart.returnDetails),
      amount: this.selectedSparePart.amount ?? null,
      unitPrice: this.selectedSparePart.unitPrice ?? null,
      valorDeVenta: this.selectedSparePart.valorDeVenta ?? null,
      country: this.text(this.selectedSparePart.country),
      netWheight: this.selectedSparePart.netWheight ?? null,
      grossWheight: this.selectedSparePart.grossWheight ?? null,
      taxNote: this.text(this.selectedSparePart.taxNote),
      ncm: this.text(this.selectedSparePart.ncm),
      codBr: this.text(this.selectedSparePart.codBr),
      packageNumber: this.text(this.selectedSparePart.packageNumber),
      exportNumber: this.text(this.selectedSparePart.exportNumber),
      comments: this.text(this.selectedSparePart.comments),
      emergentComment: this.text(this.selectedSparePart.emergentComment),
      effectiveEmergentComment: this.text(this.selectedSparePart.effectiveEmergentComment),
      shippingReceipt: this.text(this.selectedSparePart.shippingReceipt),
      interno: this.text(this.selectedSparePart.interno),
      externo: this.text(this.selectedSparePart.externo),
      color: this.text(this.selectedSparePart.color),
      isFant: !!this.selectedSparePart.isFant,
      fantUrgency: this.selectedSparePart.isFant ? normalizeFantUrgency(this.selectedSparePart.fantUrgency) : null,
      control: !!this.selectedSparePart.control,
      anulado: !!this.selectedSparePart.anulado,
      trello: !!this.selectedSparePart.trello,
      devZureo: !!this.selectedSparePart.devZureo,
      entradaSage: !!this.selectedSparePart.entradaSage,
      controleSeba: !!this.selectedSparePart.controleSeba,
      okAnulacao: !!this.selectedSparePart.okAnulacao,
      facturado: !!this.selectedSparePart.facturado,
      recompra: !!this.selectedSparePart.recompra,
      isDamaged: readIsDamagedState(this.selectedSparePart),
      needReview: readNeedReviewFlag(this.selectedSparePart),
      arrivalDate: this.selectedSparePartArrivalDate,
      physicalArrivalDateTime: this.selectedSparePartPhysicalArrivalDateTime,
      exportDate: this.selectedSparePartExportDate,
      fechaDeComunicado: this.aldanaFechaComunicadoInput,
      mlDeadline: this.selectedSparePartMlDeadline
    });
  }

  private toInputDate(value: any): string {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  }

  private toInputDateTime(value: any): string {
    return formatWallClockDateTimeForInput(value);
  }

  private parseInputDate(value: string): Date | undefined {
    if (!value) return undefined;
    const parsed = new Date(`${value}T00:00:00`);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  }

  private parseLocalDateOnly(value: string): Date | undefined {
    if (!value) return undefined;
    const parts = value.split('-');
    if (parts.length !== 3) return undefined;

    const year = Number(parts[0]);
    const month = Number(parts[1]);
    const day = Number(parts[2]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return undefined;

    const parsed = new Date(year, month - 1, day, 0, 0, 0, 0);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  }

  private parseInputDateTime(value: string): string | undefined {
    return parseWallClockDateTimeInput(value);
  }

  toggleAldanaForm(): void {
    this.showAldanaForm = !this.showAldanaForm;
  }

  selectAldanaColor(color: string): void {
    if (!this.selectedSparePart) return;
    this.selectedSparePart.color = color;
  }

  isFormDirty(): boolean {
    return this.initialComparableState != null &&
      this.selectedSparePart != null &&
      this.buildComparableState() !== this.initialComparableState;
  }

  canSaveForm(): boolean {
    if (this.duplicateMode) return true;
    if (this.selectedSparePart && this.text(this.selectedSparePart.exportNumber) !== this.exportNumberWhenOpened) {
      return true;
    }
    return this.isFormDirty();
  }

  requestDuplicate(): void {
    if (!this.selectedSparePart) return;
    this.duplicateRequested.emit(this.selectedSparePart);
    this.scrollToTopOfForm();
  }

  private scrollToTopOfForm(): void {
    setTimeout(() => {
      if (this.modalContent?.nativeElement) {
        this.modalContent.nativeElement.scrollTop = 0;
      }
    }, 0);
  }

  onDuplicateTargetOrderNumberChanged(value: string): void {
    this.duplicateTargetOrderNumber = value || '';
    this.duplicateTargetOrderErrorMessage = '';
    this.duplicateTargetOrderNumberChange.emit(this.duplicateTargetOrderNumber);
  }

  private normalizeOrderNumber(value: string | undefined | null): string {
    return (value || '').trim().toLowerCase();
  }

  private resolveOrderIdByNumber(
    orderNumberInput: string,
    onResolved: (targetOrderId: number, targetOrderNumber: string) => void,
    onFailed?: () => void
  ): void {
    const orderNumber = (orderNumberInput || '').trim();
    if (!orderNumber) {
      this.duplicateTargetOrderErrorMessage = 'Debe ingresar un número de orden destino.';
      onFailed?.();
      return;
    }

    this.duplicateTargetOrderErrorMessage = '';
    this.ordersService.getAllFilteredOrders(orderNumber).subscribe({
      next: (orders) => {
        const normalizedTarget = this.normalizeOrderNumber(orderNumber);
        const exactMatch = (orders || []).find(order =>
          this.normalizeOrderNumber(order.orderNumber) === normalizedTarget
        );
        if (!exactMatch?.orderId) {
          this.duplicateTargetOrderErrorMessage = `No existe una orden con número "${orderNumber}".`;
          onFailed?.();
          return;
        }
        this.duplicateTargetOrderErrorMessage = '';
        onResolved(exactMatch.orderId, exactMatch.orderNumber || orderNumber);
      },
      error: () => {
        this.duplicateTargetOrderErrorMessage = 'No se pudo validar la orden destino. Intente nuevamente.';
        onFailed?.();
      }
    });
  }
  
  ngAfterViewChecked(): void {
    // Forzar actualización del input cada vez que la vista se verifica
    if (this.selectedSparePart) {
      const remitoValue = this.selectedSparePart.shippingReceipt || 
                         (this.selectedSparePart as any).ShippingReceipt || 
                         this._shippingReceipt || 
                         '';
      
      if (remitoValue !== this.lastShippingReceipt) {
        this.forceUpdateShippingReceiptInput(remitoValue);
        this.lastShippingReceipt = remitoValue;
        this._shippingReceipt = remitoValue;
      }
      
      // También verificar el input directamente
      this.checkAndUpdateInput();
    }
  }
  
  // Método para verificar y actualizar el input periódicamente
  private checkAndUpdateInput(): void {
    if (!this.selectedSparePart) return;
    
    const expectedValue = this.selectedSparePart.shippingReceipt || 
                         (this.selectedSparePart as any).ShippingReceipt || 
                         this._shippingReceipt || 
                         '';
    
    // Verificar el input directamente
    const inputById = document.getElementById('shippingReceipt') as HTMLInputElement;
    if (inputById) {
      const currentValue = inputById.value || '';
      if (currentValue !== expectedValue) {
        this.forceUpdateShippingReceiptInput(expectedValue);
      }
    }
  }
  
  // Método agresivo para forzar actualización del input directamente
  private forceUpdateShippingReceiptInput(value: string): void {
    try {
      // Actualizar primero las variables internas
      this._shippingReceipt = value || '';
      if (this.selectedSparePart) {
        this.selectedSparePart.shippingReceipt = value || '';
      }
      this.lastShippingReceipt = value || '';
      
      // Método 1: Usar ViewChild si está disponible
      if (this.shippingReceiptInput && this.shippingReceiptInput.nativeElement) {
        const input = this.shippingReceiptInput.nativeElement;
        const oldValue = input.value || '';
        input.value = value || '';
        
        // Disparar eventos para que Angular detecte el cambio
        input.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
        input.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
        
        // También disparar eventos nativos del navegador
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
        if (nativeInputValueSetter) {
          nativeInputValueSetter.call(input, value || '');
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
      
      // Método 2: Buscar el input por ID como respaldo
      const inputById = document.getElementById('shippingReceipt') as HTMLInputElement;
      if (inputById) {
        const oldValue = inputById.value || '';
        inputById.value = value || '';
        
        // Disparar eventos
        inputById.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
        inputById.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
        
        // También usar el setter nativo
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
        if (nativeInputValueSetter) {
          nativeInputValueSetter.call(inputById, value || '');
          inputById.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
      
      // Método 3: Buscar por atributo name
      const inputByName = document.querySelector('input[name="shippingReceipt"]') as HTMLInputElement;
      if (inputByName) {
        const oldValue = inputByName.value || '';
        inputByName.value = value || '';
        
        // Disparar eventos
        inputByName.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
        inputByName.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
        
        // También usar el setter nativo
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
        if (nativeInputValueSetter) {
          nativeInputValueSetter.call(inputByName, value || '');
          inputByName.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
      
      // Método 4: Buscar todos los inputs y actualizar el que tenga el ID correcto
      const allInputs = document.querySelectorAll('input');
      allInputs.forEach(input => {
        if (input.id === 'shippingReceipt' || input.getAttribute('name') === 'shippingReceipt') {
          const oldValue = input.value || '';
          input.value = value || '';
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      
      // Forzar detección de cambios múltiples veces
      this.cdr.detectChanges();
      setTimeout(() => this.cdr.detectChanges(), 0);
      setTimeout(() => this.cdr.detectChanges(), 50);
      
    } catch (error) {
      console.error('❌ Error forzando actualización del input:', error);
    }
  }

  closeSparePartModal(): void {
    // Aquí puedes agregar la lógica para guardar los cambios en el backend si es necesario
    this.showLowSaleRatioConfirmModal = false;
    this.lowSaleRatioViolationLabels = [];
    this.pendingDuplicateAction = null;
    this.close.emit(); // Cerrar el modal después de guardar
  }

  confirmLowSaleRatioAndContinue(): void {
    const pendingAction = this.pendingDuplicateAction;
    this.showLowSaleRatioConfirmModal = false;
    this.lowSaleRatioViolationLabels = [];
    this.pendingDuplicateAction = null;
    if (pendingAction) {
      pendingAction();
    }
  }

  cancelLowSaleRatioAndContinueEditing(): void {
    this.showLowSaleRatioConfirmModal = false;
    this.lowSaleRatioViolationLabels = [];
    this.pendingDuplicateAction = null;
  }

  /**
   * Normaliza un valor numérico, preservando el punto como separador decimal
   * @param value - El valor a normalizar
   * @returns número normalizado
   */
  normalizeNumericValue(value: any): number {
    // Si es null, undefined o vacío, devolver 0
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    
    // Si ya es un número, solo devolvemos el mismo valor
    if (typeof value === 'number') {
      return value;
    }
    
    // Convertir a string si no lo es
    let stringValue = value.toString();
    
    // Tratamos el formato específico dependiendo de si es formato europeo (1.234,56) o estadounidense (1,234.56)
    // Verificamos si hay un punto que parece ser un separador decimal (si es el último punto)
    const lastDotIndex = stringValue.lastIndexOf('.');
    const lastCommaIndex = stringValue.lastIndexOf(',');
    
    // Si hay un punto y está después de la última coma, o no hay comas, 
    // asumimos que el punto es el separador decimal
    if (lastDotIndex > -1 && (lastDotIndex > lastCommaIndex || lastCommaIndex === -1)) {
      // Formato con punto decimal (1,234.56 o 1234.56)
      // Eliminamos las comas (que serían separadores de miles)
      stringValue = stringValue.replace(/,/g, '');
    } else if (lastCommaIndex > -1) {
      // Formato con coma decimal (1.234,56 o 1234,56)
      // Primero eliminamos los puntos (que serían separadores de miles)
      stringValue = stringValue.replace(/\./g, '');
      // Luego reemplazamos la coma por punto para el formato estándar
      stringValue = stringValue.replace(/,/g, '.');
    }
    
    // Asegurar que solo hay un punto decimal
    const parts = stringValue.split('.');
    if (parts.length > 2) {
      // Si hay más de un punto, conservamos solo el primero
      stringValue = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Asegurar exactamente 2 decimales
    if (parts.length === 2) {
      const integerPart = parts[0];
      let decimalPart = parts[1];
      
      // Si hay más de 2 decimales, truncamos a 2
      if (decimalPart.length > 2) {
        decimalPart = decimalPart.substring(0, 2);
      }
      
      // Si hay menos de 2 decimales, completamos con ceros
      if (decimalPart.length < 2) {
        decimalPart = decimalPart.padEnd(2, '0');
      }
      
      stringValue = integerPart + '.' + decimalPart;
    } else if (!stringValue.includes('.')) {
      // Si no hay punto decimal, agregamos .00
      stringValue = stringValue + '.00';
    }
    
    // Convertir a número y verificar que sea válido
    const numValue = parseFloat(stringValue);
    if (isNaN(numValue)) {
      return 0;
    }
    
    // Asegurar que el valor sea un número con 2 decimales exactos
    return parseFloat(numValue.toFixed(2));
  }

  /**
   * Maneja la entrada de valores numéricos en campos de texto
   * @param event - Evento de entrada
   */
  // Método para normalizar inputs de texto en la UI usando la nueva utilidad
  handleNumericInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    CurrencyFormatter.handleInput(inputElement, { maxDecimals: 2, allowNegative: false });
  }

  // Método para formatear al perder el foco
  handleNumericBlur(event: any): void {
    const inputElement = event.target as HTMLInputElement;
    CurrencyFormatter.handleBlur(inputElement, { maxDecimals: 2, minDecimals: 2, allowNegative: false });
  }

  // Método para preparar el campo al recibir foco
  handleNumericFocus(event: any): void {
    const inputElement = event.target as HTMLInputElement;
    CurrencyFormatter.handleFocus(inputElement);
  }

  // Método original mantenido para compatibilidad (renombrado)
  handleNumericInputOriginal(event: Event): void {
    const input = event.target as HTMLInputElement;
    const initialValue = input.value;
    
    // Preservar posición del cursor
    const cursorPosition = input.selectionStart;
    
    // Permitir solo dígitos, comas, puntos y el signo negativo al principio
    let value = initialValue.replace(/[^0-9,.\\-]/g, '');
    
    // Verificar que el signo negativo solo esté al inicio
    if (value.indexOf('-') > 0) {
      value = value.replace(/-/g, '');
    }
    
    // Determinar el formato (europeo o americano) por la posición del último punto y coma
    const lastDotIndex = value.lastIndexOf('.');
    const lastCommaIndex = value.lastIndexOf(',');
    
    // Si hay un punto y está después de la última coma, o no hay comas,
    // asumimos formato con punto decimal (1,234.56)
    if (lastDotIndex > -1 && (lastDotIndex > lastCommaIndex || lastCommaIndex === -1)) {
      // Asegurarnos de que solo hay un punto decimal
      const parts = value.split('.');
      if (parts.length > 2) {
        // Si hay más de un punto, conservamos solo el último
        value = parts[0] + '.' + parts.slice(1).join('');
      }
      
      // Limitar a 2 decimales
      if (parts.length > 1 && parts[1].length > 2) {
        parts[1] = parts[1].substring(0, 2);
        value = parts[0] + '.' + parts[1];
      }
    } 
    // Si hay una coma y está después del último punto, o no hay puntos,
    // asumimos formato con coma decimal (1.234,56)
    else if (lastCommaIndex > -1 && (lastCommaIndex > lastDotIndex || lastDotIndex === -1)) {
      // Asegurarnos de que solo hay una coma decimal
      const parts = value.split(',');
      if (parts.length > 2) {
        // Si hay más de una coma, conservamos solo la última
        value = parts[0] + ',' + parts.slice(1).join('');
      }
      
      // Limitar a 2 decimales
      if (parts.length > 1 && parts[1].length > 2) {
        parts[1] = parts[1].substring(0, 2);
        value = parts[0] + ',' + parts[1];
      }
    }
    
    // Si el valor ha cambiado, actualizar el campo
    if (value !== initialValue) {
      const oldLength = initialValue.length;
      input.value = value;
      
      // Restaurar posición del cursor, ajustando si se modificó el valor
      if (cursorPosition !== null) {
        const newPosition = Math.min(cursorPosition + (value.length - oldLength), value.length);
        input.setSelectionRange(newPosition, newPosition);
      }
    }
  }

  onFantCheckboxChanged(isFant: boolean): void {
    if (!this.selectedSparePart) return;
    this.selectedSparePart.fantUrgency = defaultFantUrgency(isFant, null);
  }

  setFantUrgency(urgency: FantUrgency): void {
    if (!this.selectedSparePart?.isFant) return;
    const current = normalizeFantUrgency(this.selectedSparePart.fantUrgency);
    this.selectedSparePart.fantUrgency = current === urgency ? null : urgency;
  }

  saveSparePartChanges(): void {
    if(this.selectedSparePart){ 
      // Validar y convertir la fecha
      if (this.selectedSparePartArrivalDate) {
        const parsedExpectedArrivalDate = this.parseLocalDateOnly(this.selectedSparePartArrivalDate);
        if (parsedExpectedArrivalDate) {
          this.selectedSparePart.expectedArrivalDate = parsedExpectedArrivalDate;
        }
      }
      this.selectedSparePart.arrivalDate = this.parseInputDateTime(this.selectedSparePartPhysicalArrivalDateTime);
      this.selectedSparePart.fechaExportacion = this.parseInputDate(this.selectedSparePartExportDate);
      this.selectedSparePart.fechaDeComunicado = this.parseInputDate(this.aldanaFechaComunicadoInput);
      this.selectedSparePart.mlDeadline = this.parseInputDate(this.selectedSparePartMlDeadline);

      // Hacer una copia para las actualizaciones
      const sparePartToUpdate = {...this.selectedSparePart};
      sparePartToUpdate.fantUrgency = sparePartToUpdate.isFant
        ? normalizeFantUrgency(sparePartToUpdate.fantUrgency)
        : null;
      sparePartToUpdate.needReview = readNeedReviewFlag(sparePartToUpdate);
      sparePartToUpdate.isDamaged = readIsDamagedState(sparePartToUpdate);
      
      // Normalizar todos los valores numéricos
      sparePartToUpdate.unitPrice = this.normalizeNumericValue(sparePartToUpdate.unitPrice);
      sparePartToUpdate.valorDeVenta = this.normalizeNumericValue(sparePartToUpdate.valorDeVenta);
      sparePartToUpdate.netWheight = this.normalizeNumericValue(sparePartToUpdate.netWheight);
      sparePartToUpdate.grossWheight = this.normalizeNumericValue(sparePartToUpdate.grossWheight);
      sparePartToUpdate.amount = Math.max(1, this.normalizeNumericValue(sparePartToUpdate.amount));
      
      // Asegurar que unitPrice quede con 2 decimales, permitiendo 0 para aplicar regla de control.
      sparePartToUpdate.unitPrice = parseFloat(sparePartToUpdate.unitPrice.toFixed(2));
      sparePartToUpdate.valorDeVenta = parseFloat(sparePartToUpdate.valorDeVenta.toFixed(2));
      sparePartToUpdate.netWheight = parseFloat(sparePartToUpdate.netWheight.toFixed(2));
      sparePartToUpdate.grossWheight = parseFloat(sparePartToUpdate.grossWheight.toFixed(2));

      if (this.duplicateMode) {
        const saleValueError = getSaleValueValidationError(
          sparePartToUpdate.valorDeVenta,
          sparePartToUpdate.sparePartName
        );
        if (saleValueError) {
          alert(saleValueError);
          return;
        }
      }

      // Permitir valores vacíos en la edición - solo validar si se mantiene la estructura básica
      
      // Mostrar indicador de carga
      const loadingIndicator = document.createElement('div');
      loadingIndicator.id = 'spare-part-saving-indicator';
      loadingIndicator.className = 'fixed top-4 right-4 bg-blue-500 text-white px-4 py-2 rounded shadow-lg z-50 flex items-center';
      loadingIndicator.innerHTML = `
        <div class="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
        <span>Guardando cambios...</span>
      `;
      document.body.appendChild(loadingIndicator);
      
      if (this.duplicateMode) {
        const performDuplicate = () => this.resolveOrderIdByNumber(this.duplicateTargetOrderNumber, (targetOrderId, targetOrderNumber) => {
          const sparePartToCreate = applyPostEmergentCommentDuplicateDefaults({
            ...sparePartToUpdate,
            sparePartId: 0,
            packageNumber: '',
            exportNumber: '',
            shippingReceipt: '',
            lastExportedAt: undefined,
            arrivalDate: undefined,
            fechaExportacion: undefined
          } as SparePartModel);

          this.sparePartService.addSparePart(sparePartToCreate, targetOrderId).subscribe({
            next: () => {
              const indicator = document.getElementById('spare-part-saving-indicator');
              if (indicator) indicator.remove();
              this.sparePartUpdated.emit(sparePartToCreate);

              const successMessage = document.createElement('div');
              successMessage.id = 'spare-part-success-message';
              successMessage.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50';
              successMessage.innerHTML = `Repuesto duplicado correctamente en la orden #${targetOrderNumber}`;
              document.body.appendChild(successMessage);

              setTimeout(() => {
                const message = document.getElementById('spare-part-success-message');
                if (message) message.remove();
              }, 3000);

              this.closeSparePartModal();
            },
            error: (error) => {
              console.error('Error al duplicar el repuesto:', error);
              const indicator = document.getElementById('spare-part-saving-indicator');
              if (indicator) indicator.remove();
              const backendErrorMessage = this.getBackendErrorMessage(error, 'Error desconocido');

              const errorMessage = document.createElement('div');
              errorMessage.id = 'spare-part-error-message';
              errorMessage.className = 'fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded shadow-lg z-50';
              errorMessage.innerHTML = `Error al guardar el repuesto duplicado: ${backendErrorMessage}`;
              document.body.appendChild(errorMessage);

              setTimeout(() => {
                const message = document.getElementById('spare-part-error-message');
                if (message) message.remove();
              }, 5000);
            }
          });
        }, () => {
          const indicator = document.getElementById('spare-part-saving-indicator');
          if (indicator) indicator.remove();
        });

        const lowSaleRatioViolations = collectLowSaleRatioViolations([{
          sparePartName: sparePartToUpdate.sparePartName,
          productCode: sparePartToUpdate.productCode,
          orderNumber: this.duplicateTargetOrderNumber || sparePartToUpdate.orderNumber || '',
          siniestro: sparePartToUpdate.siniestro || '',
          unitPrice: sparePartToUpdate.unitPrice,
          valorDeVenta: sparePartToUpdate.valorDeVenta
        }]);

        if (lowSaleRatioViolations.length > 0) {
          const indicator = document.getElementById('spare-part-saving-indicator');
          if (indicator) indicator.remove();
          this.lowSaleRatioViolationLabels = lowSaleRatioViolations.map(v => v.label);
          this.pendingDuplicateAction = performDuplicate;
          this.showLowSaleRatioConfirmModal = true;
          return;
        }

        performDuplicate();
        return;
      }

      // Realizar la actualización
      const orderId = this.selectedSparePart.orderId;
      this.sparePartService.updateSparePart(sparePartToUpdate, orderId).subscribe({
        next: (response) => {
          this.showEmergentCommentAlertGroups(response?.emergentCommentAlerts, 'Actualización de repuesto');
          // Quitar el indicador de carga
          const indicator = document.getElementById('spare-part-saving-indicator');
          if (indicator) indicator.remove();

          if (response?.updatedFechaDeComunicado) {
            sparePartToUpdate.fechaDeComunicado = new Date(response.updatedFechaDeComunicado);
            this.aldanaFechaComunicadoInput = this.toInputDate(sparePartToUpdate.fechaDeComunicado);
          }
          
          // Emitir el repuesto que se guardó (el backend no devuelve el objeto actualizado)
          this.sparePartUpdated.emit(sparePartToUpdate);
          
          // Mostrar mensaje de éxito
          const successMessage = document.createElement('div');
          successMessage.id = 'spare-part-success-message';
          successMessage.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50';
          successMessage.innerHTML = 'Repuesto actualizado correctamente';
          document.body.appendChild(successMessage);
          
          // Eliminar el mensaje después de 3 segundos
          setTimeout(() => {
            const message = document.getElementById('spare-part-success-message');
            if (message) message.remove();
          }, 3000);
          
          this.closeSparePartModal();
        },
        error: (error) => {
          console.error('Error al guardar el repuesto:', error);
          
          // Quitar el indicador de carga
          const indicator = document.getElementById('spare-part-saving-indicator');
          if (indicator) indicator.remove();
          const backendErrorMessage = this.getBackendErrorMessage(error, 'Error desconocido');
          
          // Mostrar mensaje de error
          const errorMessage = document.createElement('div');
          errorMessage.id = 'spare-part-error-message';
          errorMessage.className = 'fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded shadow-lg z-50';
          errorMessage.innerHTML = `Error al guardar los cambios: ${backendErrorMessage}`;
          document.body.appendChild(errorMessage);
          
          // Eliminar el mensaje después de 5 segundos
          setTimeout(() => {
            const message = document.getElementById('spare-part-error-message');
            if (message) message.remove();
          }, 5000);
        }
      });
    }
  }

  private getBackendErrorMessage(error: any, fallback: string): string {
    const errorBody = error?.error;
    const validationErrors = errorBody?.errors;

    if (validationErrors && typeof validationErrors === 'object') {
      const collectedMessages: string[] = [];
      Object.keys(validationErrors).forEach((fieldKey) => {
        const fieldMessages = validationErrors[fieldKey];
        if (Array.isArray(fieldMessages)) {
          fieldMessages.forEach((msg) => {
            if (typeof msg === 'string' && msg.trim() !== '') {
              collectedMessages.push(msg.trim());
            }
          });
        }
      });

      if (collectedMessages.length > 0) {
        return collectedMessages.join(' | ');
      }
    }

    if (typeof errorBody === 'string' && errorBody.trim() !== '') {
      return errorBody;
    }
    if (errorBody?.message && typeof errorBody.message === 'string') {
      return errorBody.message;
    }
    if (error?.message && typeof error.message === 'string') {
      return error.message;
    }
    return fallback;
  }

  private showEmergentCommentAlertGroups(alertGroups: any[] | undefined, _context: string): void {
    if (!Array.isArray(alertGroups) || alertGroups.length === 0) {
      return;
    }

    const lines: string[] = [];
    alertGroups.forEach((group, groupIndex) => {
      const comment = (group?.comment || '').toString().trim();
      if (!comment) {
        return;
      }

      const items = Array.isArray(group?.items) ? group.items : [];
      const names = Array.from(new Set(
        items
          .map((item: any) => (item?.sparePartName || '').toString().trim())
          .filter((name: string) => name !== '')
      ));
      const namesText = names.length > 0 ? ` - ${names.join(', ')}` : '';
      lines.push(`${groupIndex + 1}) "${comment}"${namesText}`);
    });

    if (lines.length === 0) {
      return;
    }

    const previousNotification = document.getElementById('spare-part-emergent-comment-alert');
    if (previousNotification) {
      previousNotification.remove();
    }

    const notification = document.createElement('div');
    notification.id = 'spare-part-emergent-comment-alert';
    notification.className = 'fixed top-24 right-4 bg-amber-500 text-white px-4 py-3 rounded shadow-lg z-[10000] max-w-xl whitespace-pre-line';

    const title = document.createElement('div');
    title.className = 'font-semibold mb-1';
    title.textContent = 'Comentario emergente';

    const body = document.createElement('div');
    body.textContent = lines.join('\n');

    notification.appendChild(title);
    notification.appendChild(body);
    document.body.appendChild(notification);

    setTimeout(() => {
      const message = document.getElementById('spare-part-emergent-comment-alert');
      if (message) message.remove();
    }, 7000);
  }

  deleteSparePart(): void {
    if (this.selectedSparePart) {
      if (sparePartHasPackageOrPrintedLabel(this.selectedSparePart)) {
        this.showErrorMessage = true;
        this.errorMessage = SPARE_PART_DELETE_BLOCKED_MESSAGE;
        setTimeout(() => {
          this.showErrorMessage = false;
        }, 5000);
        return;
      }

      if (confirm('¿Estás seguro de que deseas eliminar este repuesto?')) {
        const orderId = this.selectedSparePart['orderId'];
        if (!orderId) {
          this.showErrorMessage = true;
          this.errorMessage = 'No se pudo determinar la orden asociada a este repuesto. No se puede eliminar.';
          setTimeout(() => {
            this.showErrorMessage = false;
          }, 5000);
          return;
        }

        this.sparePartService.deleteSparePart(this.selectedSparePart.sparePartId, orderId).subscribe(
          () => {
            this.showSuccessMessage = true;
            this.successMessage = 'Repuesto eliminado exitosamente';
            if (this.selectedSparePart) {
              this.sparePartDeleted.emit(this.selectedSparePart);
            }
            setTimeout(() => {
              this.showSuccessMessage = false;
              this.closeSparePartModal();
            }, 2000);
          },
          (error) => {
            this.showErrorMessage = true;
            this.errorMessage = readApiErrorMessage(error, 'Error al eliminar el repuesto');
            setTimeout(() => {
              this.showErrorMessage = false;
            }, 5000);
          }
        );
      }
    }
  }
}
